import {
    JBI_UI_REGION,
    type CurriculumItemCtaCardProps,
    type JBIAISummaryProps,
    type JBIVideoCardProps,
    type JbiLearningProgressCardTrackingProps,
} from '@udemy/react-my-learning-unit';

import {JbiEnhancedLectureVariant} from 'src/lib/experimentation-constants';
import {NextCurriculumItem, UserEnrollmentsData} from 'src/types/page-props';

type CourseEnrollment = NonNullable<
    NonNullable<
        NonNullable<UserEnrollmentsData['me']['enrollments']>['courseEnrollmentsPaged']
    >['courseEnrollments'][number]
>;

export type CourseEnrollmentWithNextItem = CourseEnrollment & {
    course: CourseEnrollment['course'] & {
        nextCurriculumItem: NextCurriculumItem;
    };
};

type CourseContext = Pick<CourseEnrollment['course'], 'urlCourseTaking' | 'images'>;

type CourseContextForCtaCard = Pick<
    CourseEnrollment['course'],
    'title' | 'headline' | 'urlCourseTaking' | 'images'
>;

type VideoNextItem = Extract<
    NextCurriculumItem,
    {__typename: 'VideoLecture'} | {__typename: 'VideoMashupLecture'}
>;

type VideoLectureNextItem = Extract<NextCurriculumItem, {__typename: 'VideoLecture'}>;

type CourseContextForSummary = Pick<
    CourseEnrollment['course'],
    'title' | 'urlCourseTaking' | 'images' | 'instructors'
>;

type GraphQLMediaSource = NonNullable<NonNullable<VideoNextItem['asset']>['mediaSources']>[number];

const MEDIA_TYPE_TO_MIME: Record<string, string> = {
    APPLICATION_X_MPEGURL: 'application/x-mpegURL',
    APPLICATION_DASH_XML: 'application/dash+xml',
    VIDEO_MP4: 'video/mp4',
    AUDIO_MP3: 'audio/mpeg',
};

/** Returns true when the next curriculum item is a video lecture. */
export function isNextItemVideoLecture(nextItem: NextCurriculumItem): nextItem is VideoNextItem {
    return nextItem.__typename === 'VideoLecture' || nextItem.__typename === 'VideoMashupLecture';
}

/** Narrows a filtered course enrollment to one with a defined next curriculum item. */
export function asEnrollmentWithNextItem(
    enrollment: CourseEnrollment | null | undefined,
): CourseEnrollmentWithNextItem | null {
    const nextItem = enrollment?.course?.nextCurriculumItem;

    if (!enrollment || !nextItem) {
        return null;
    }

    return {
        ...enrollment,
        course: {
            ...enrollment.course,
            nextCurriculumItem: nextItem,
        },
    };
}

function getNextItemUrl(nextItem: NextCurriculumItem, courseUrl?: string | null): string {
    if ('urlLanding' in nextItem && nextItem.urlLanding) {
        return nextItem.urlLanding;
    }

    return courseUrl ?? '/';
}

function getNextItemDuration(nextItem: NextCurriculumItem): number {
    return 'durationInSeconds' in nextItem ? nextItem.durationInSeconds ?? 0 : 0;
}

function getNextItemThumbnail(
    nextItem: NextCurriculumItem,
    courseImage?: string | null,
): string | undefined {
    const itemThumbnail = 'thumbnail' in nextItem ? nextItem.thumbnail : undefined;

    return itemThumbnail || courseImage || undefined;
}

function getLastWatchedSecond(nextItem: NextCurriculumItem): number {
    return 'lastWatchedSecond' in nextItem ? nextItem.lastWatchedSecond ?? 0 : 0;
}

function mapMediaSources(
    mediaSources?: GraphQLMediaSource[] | null,
): JBIVideoCardProps['mediaSources'] {
    if (!mediaSources?.length) {
        return [];
    }

    return mediaSources.map(({src, mediaType, label}) => ({
        src,
        type: MEDIA_TYPE_TO_MIME[mediaType] ?? mediaType,
        label,
    }));
}

/** Maps GraphQL enrollment data to CurriculumItemCtaCard props. */
export function toCurriculumItemCtaCardProps(
    course: CourseContextForCtaCard,
    nextItem: NextCurriculumItem,
): CurriculumItemCtaCardProps {
    const durationInSeconds = getNextItemDuration(nextItem);
    const thumbnailUrl = getNextItemThumbnail(nextItem, course.images?.px240x135);

    return {
        typename: nextItem.__typename,
        itemTitle: nextItem.title,
        itemUrl: getNextItemUrl(nextItem, course.urlCourseTaking),
        courseTitle: course.title,
        ...(durationInSeconds > 0 ? {durationInSeconds} : {}),
        ...(course.headline ? {description: course.headline} : {}),
        ...(thumbnailUrl ? {thumbnailUrl} : {}),
    };
}

function toLearningProgressCardTrackingProps(
    courseTrackingId: string,
    nextItem: NextCurriculumItem,
    uiRegion: string,
): JbiLearningProgressCardTrackingProps {
    return {
        courseTrackingId,
        curriculumId: Number.parseInt(nextItem.id, 10),
        uiRegion,
    };
}

/** Maps GraphQL enrollment data to JBIVideoCard props. */
export function toJBIVideoCardProps(
    course: CourseContext,
    nextItem: VideoNextItem,
): JBIVideoCardProps {
    const asset = nextItem.asset ?? undefined;

    return {
        lectureTitle: nextItem.title,
        lectureUrl: getNextItemUrl(nextItem, course.urlCourseTaking),
        startTime: getLastWatchedSecond(nextItem),
        duration: getNextItemDuration(nextItem),
        assetId: Number.parseInt(asset?.id ?? nextItem.id, 10),
        thumbnailUrl: getNextItemThumbnail(nextItem, course.images?.px240x135),
        mediaSources: mapMediaSources(asset?.mediaSources),
        mediaLicenseToken: asset?.accessToken ?? undefined,
    };
}

export const toJBIAISummaryProps = (
    course: CourseContextForSummary,
    nextItem: VideoLectureNextItem,
): JBIAISummaryProps => ({
    course: {
        title: course.title,
        urlCourseTaking: course.urlCourseTaking ?? undefined,
        images: course.images as unknown as JBIAISummaryProps['course']['images'],
        instructors: course.instructors as unknown as JBIAISummaryProps['course']['instructors'],
    },
    ...(nextItem.aiSummary ? {summary: {text: nextItem.aiSummary}} : {}),
});

export type TrackedJbiCardConfig =
    | {
          type: 'video';
          cardProps: JBIVideoCardProps;
          trackingProps: JbiLearningProgressCardTrackingProps;
      }
    | {
          type: 'curriculum';
          cardProps: CurriculumItemCtaCardProps;
          trackingProps: JbiLearningProgressCardTrackingProps;
      }
    | {
          type: 'summary';
          cardProps: JBIAISummaryProps;
      };

const toCurriculumCardConfig = (
    course: CourseContextForCtaCard,
    nextItem: NextCurriculumItem,
    generateId: () => string,
) => ({
    type: 'curriculum' as const,
    cardProps: toCurriculumItemCtaCardProps(course, nextItem),
    trackingProps: toLearningProgressCardTrackingProps(
        generateId(),
        nextItem,
        JBI_UI_REGION.CURRICULUM,
    ),
});

interface ResolveTrackedJbiCardParams {
    isLoading: boolean;
    variant: JbiEnhancedLectureVariant | undefined;
    enrollment: CourseEnrollmentWithNextItem | null;
    generateId: () => string;
}

/** Resolves which tracked JBI card to render for the current experiment variant. */
export function resolveTrackedJbiCard({
    isLoading,
    variant,
    enrollment,
    generateId,
}: ResolveTrackedJbiCardParams): TrackedJbiCardConfig | null {
    if (isLoading || !enrollment) return null;
    if (variant === 'control') return null;

    const {course} = enrollment;
    const nextItem = course.nextCurriculumItem;

    if (variant === 'summary') {
        if (nextItem.__typename === 'VideoLecture') {
            return {
                type: 'summary',
                cardProps: toJBIAISummaryProps(course, nextItem),
            };
        }

        return toCurriculumCardConfig(course, nextItem, generateId);
    }

    if (variant === 'video' && isNextItemVideoLecture(nextItem)) {
        return {
            type: 'video',
            cardProps: toJBIVideoCardProps(course, nextItem),
            trackingProps: toLearningProgressCardTrackingProps(
                generateId(),
                nextItem,
                JBI_UI_REGION.VIDEO,
            ),
        };
    }

    if (variant === 'curriculum' || variant === 'video') {
        return toCurriculumCardConfig(course, nextItem, generateId);
    }

    return null;
}
